import CourseProfessor from 'components/professors/CourseProfessor';
import TabDocumentation from './components/TabDocumentation';
import TabStudents from './components/TabStudents';
import { CoursesIconComponent } from 'assets/icons/courses';
import { EditLayout } from 'components/layout/layout/EditLayout';
import { Flex, Tabs, Tag } from 'antd';
import {
  GraduateCourseFeaturesPatch,
  TabFeatures
} from './components/TabFeatures';
import { HttpError, useCan } from '@refinedev/core';
import { IGraduateCourseShow } from 'interfaces/graduate_courses';
import { PageHeader } from 'components/professor_area/PageHeader';
import { TabAcademicCalendar } from 'components/courses/TabAcademicCalendar';
import { TabDisciplines } from './components/TabDisciplines';
import { TabForum } from './components/TabForum';
import { TabGeneralInfo } from './components/TabGeneralInfo';
import { TabInstitutionalMaterials } from 'components/courses/TabInstitutionalMaterials';
import { getTenant, isFeatureEnabled } from 'services/tenants';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from '@refinedev/antd';

export const GraduateCourseEdit = () => {
  const [activeTab, setActiveTab] = useState('info');
  const tenant = getTenant();
  const { data: canShow } = useCan({
    resource: 'graduate_courses',
    action: 'show'
  });
  const { data: canEdit } = useCan({
    resource: 'graduate_courses',
    action: 'edit'
  });
  const { data: canListDisciplineModules } = useCan({
    resource: 'graduate_disciplines_modules',
    action: 'list'
  });
  const { data: canListDocumentTemplates } = useCan({
    resource: 'document_templates',
    action: 'list'
  });
  const { data: canListProfessorCourses } = useCan({
    resource: 'professor_courses',
    action: 'list'
  });
  const { data: canListForums } = useCan({
    resource: 'forums',
    action: 'list'
  });
  const { data: canListAcademicCalendars } = useCan({
    resource: 'graduate/courses/academic_calendars',
    action: 'list'
  });
  const { data: canCreateAcademicCalendars } = useCan({
    resource: 'graduate/courses/academic_calendars',
    action: 'create'
  });
  const { data: canEditAcademicCalendars } = useCan({
    resource: 'graduate/courses/academic_calendars',
    action: 'edit'
  });
  const { data: canDeleteAcademicCalendars } = useCan({
    resource: 'graduate/courses/academic_calendars',
    action: 'delete'
  });

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const { formProps, saveButtonProps, queryResult } = useForm<
    IGraduateCourseShow,
    HttpError,
    IGraduateCourseShow | GraduateCourseFeaturesPatch
  >({
    redirect: 'edit'
  });

  const onFinish = useCallback(
    (data: IGraduateCourseShow | GraduateCourseFeaturesPatch) => {
      formProps.onFinish!({ ...data });
    },
    [formProps.onFinish]
  );

  const record = useMemo(() => queryResult?.data?.data, [queryResult]);
  const canSeeCourseContentTab = Boolean(canListDisciplineModules?.can);

  return (
    <EditLayout
      footerButtonProps={{
        style: {
          justifyContent: 'flex-end',
          display:
            ['info', 'features', 'professor'].includes(activeTab) &&
            canEdit?.can
              ? 'flex'
              : 'none'
        }
      }}
      saveButtonProps={
        canEdit?.can &&
        !['features', 'academic_calendar', 'institutional_materials'].includes(
          activeTab
        )
          ? saveButtonProps
          : { hidden: true }
      }
    >
      <PageHeader
        title={record?.name}
        icon={CoursesIconComponent}
        meta={
          <Flex>
            <Tag>
              {record?.kind === 'graduate' ? 'Pós Graduação' : 'Graduação'}
            </Tag>
          </Flex>
        }
      />

      <Tabs
        defaultActiveKey={activeTab}
        onChange={handleTabChange}
        className="custom-tabs"
      >
        {canEdit?.can && (
          <Tabs.TabPane tab="Informações gerais" key="info">
            <TabGeneralInfo formProps={formProps} record={record} />
          </Tabs.TabPane>
        )}

        {canSeeCourseContentTab && (
          <Tabs.TabPane tab="Módulos e Disciplinas" key="courseContent">
            <TabDisciplines
              isUndergraduate={record?.kind === 'undergraduate'}
            />
          </Tabs.TabPane>
        )}

        {canEdit?.can && record?.id && (
          <Tabs.TabPane
            tab="Materiais Institucionais"
            key="institutional_materials"
          >
            <TabInstitutionalMaterials
              parentKind="graduate_course"
              record={record}
            />
          </Tabs.TabPane>
        )}

        {canShow?.can && (
          <Tabs.TabPane tab="Alunos" key="courseStudents">
            <TabStudents />
          </Tabs.TabPane>
        )}

        {canListDocumentTemplates?.can && (
          <Tabs.TabPane tab="Documentação" key="courseDocumentation">
            <TabDocumentation />
          </Tabs.TabPane>
        )}

        {canListProfessorCourses?.can && (
          <Tabs.TabPane tab="Coordenador do curso" key="professor">
            {queryResult?.data?.data && (
              <CourseProfessor course={queryResult.data.data} />
            )}
          </Tabs.TabPane>
        )}

        {canListForums?.can && !tenant.disabledFeatures?.includes('forums') && (
          <Tabs.TabPane tab="Fóruns" key="forum">
            <TabForum />
          </Tabs.TabPane>
        )}
        {canEdit?.can && (
          <Tabs.TabPane tab="Funcionalidades" key="features">
            <TabFeatures
              formProps={formProps}
              record={record}
              onFinish={onFinish}
            />
          </Tabs.TabPane>
        )}

        {isFeatureEnabled('course_academic_calendar') &&
          canListAcademicCalendars?.can && (
            <Tabs.TabPane tab="Calendário Acadêmico" key="academic_calendar">
              <TabAcademicCalendar
                resourcePrefix="graduate/courses"
                canList={Boolean(canListAcademicCalendars?.can)}
                canCreate={Boolean(canCreateAcademicCalendars?.can)}
                canEdit={Boolean(canEditAcademicCalendars?.can)}
                canDelete={Boolean(canDeleteAcademicCalendars?.can)}
              />
            </Tabs.TabPane>
          )}
      </Tabs>
    </EditLayout>
  );
};
